import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import Backdrop from "../../../UI/Backdrop/Backdrop";
import IntegrationMarketModal from "../Modal/IntegrationMarketModal";
import Field from "../../../../Pages/Settings/Content/Components/Field";
import CustomSelect from "../../../common/CustomSelect/CustomSelect";

import {
  RequestSetConfig,
  RequestDeleteConfig,
  RequestUpdateConfig,
} from "../../../../store/actions/integrationsActions";

import "./styles.scss";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const MAX_SIZE_LIMIT_MB = 3584;
const DEFAULT_MAX_SIZE_MB = 1024;

const clampMaxsize = (value) =>
  Number(value) > MAX_SIZE_LIMIT_MB ? MAX_SIZE_LIMIT_MB : value;

const BLOCK_OPTIONS = [
  { id: 1, value: "Enabled" },
  { id: 3, value: "Delete + Note" },
  { id: 0, value: "Disabled" },
];

const TYPE_OPTIONS = [
  { id: "onedrive", value: "Online" },
  { id: "sharepoint-onprem", value: "On-Prem" },
];

const VERIFY_OPTIONS = [
  { id: "yes", value: "Yes" },
  { id: "no", value: "No" },
];

const RCLONE_PARAMS =
  "--vfs-cache-mode=writes --no-checksum --daemon --dir-cache-time=6s --vfs-cache-poll-interval=4s";

const normalizeLibraryUrl = (value) => {
  let url = value.trim();
  if (!url.includes("://")) url = "https://" + url;
  try {
    const parsed = new URL(url);
    let path = decodeURIComponent(parsed.pathname);
    const cut = path.toLowerCase().indexOf("/forms/");
    if (cut >= 0) path = path.slice(0, cut);
    return parsed.protocol + "//" + parsed.host + path.replace(/\/+$/, "");
  } catch (e) {
    return value.trim();
  }
};

const RclshareSettings = ({ show, setShow, dataTmp = 0, setActiveInt }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [authority, setAuthority] = useState("");
  const [clientid, setClientid] = useState("");
  const [secret, setSecret] = useState("");
  const [secretid, setSecretid] = useState("");
  const [driveid, setDriveid] = useState("");
  const [type, setType] = useState(TYPE_OPTIONS[0]);
  const [user, setUser] = useState("");
  const [server, setServer] = useState("");
  const [bulk, setBulk] = useState(16);
  const [block, setBlock] = useState(BLOCK_OPTIONS[0]);
  const [maxsize, setMaxsize] = useState(DEFAULT_MAX_SIZE_MB);
  const [tlsVerify, setTlsVerify] = useState(VERIFY_OPTIONS[0]);
  const [caCert, setCaCert] = useState("");
  const [confType, setConfType] = useState(false);
  const [hoverBack, setHoverBack] = useState(false);

  const getConfig = useSelector((state) => state.integrations.getConfig);
  const setConfig = useSelector((state) => state.integrations.setConfig);
  const deleteConfig = useSelector((state) => state.integrations.deleteConfig);
  const loadingUpdate = useSelector((state) => state.integrations.loadingUpdate);
  const loading = useSelector((state) => state.integrations.loading);

  let query = useQuery();
  let confTmp = parseInt(dataTmp);

  const closePopupHandler = () => {
    cleanData();
    setShow(false);
    setActiveInt("");
    history.push("/integration-market");
  };

  const cleanData = () => {
    setAuthority("");
    setClientid("");
    setSecret("");
    setSecretid("");
    setDriveid("");
    setType(TYPE_OPTIONS[0]);
    setUser("");
    setServer("");
    setBulk(16);
    setBlock(BLOCK_OPTIONS[0]);
    setMaxsize(DEFAULT_MAX_SIZE_MB);
    setTlsVerify(VERIFY_OPTIONS[0]);
    setCaCert("");
  };

  useEffect(() => {
    if (query.get("t") === "add") {
      setConfType(true);
    } else if (query.get("t") === null) {
      setHoverBack(false);
      cleanData();
    } else {
      setConfType(false);
    }
  }, [query]);

  useEffect(() => {
    if (deleteConfig === "Success" && query.get("conf") === "rclshare" && !loadingUpdate) {
      cleanData();
    }
  }, [deleteConfig]);

  useEffect(() => {
    if (setConfig === "Success" && query.get("conf") === "rclshare" && !loadingUpdate) {
      cleanData();
    }
  }, [setConfig]);

  useEffect(() => {
    if (query.get("conf") === "rclshare") {
      if (getConfig) {
        const configData = getConfig.config[confTmp];
        if (configData) {
          setAuthority(configData?.authority);
          setClientid(configData?.client_id);
          setSecret(configData?.secret);
          setSecretid(configData?.["secret-id"]);
          setDriveid(configData?.drive_id);
          setType(TYPE_OPTIONS.find((opt) => opt.id === configData?.type) || TYPE_OPTIONS[0]);
          setUser(configData?.user ?? "");
          setServer(configData?.server);
          setBulk(configData?.bulk ?? 16);
          setBlock(
            BLOCK_OPTIONS.find((opt) => opt.id === Number(configData?.block ?? 1)) ||
              BLOCK_OPTIONS[0],
          );
          setMaxsize(clampMaxsize(configData?.maxsize ?? DEFAULT_MAX_SIZE_MB));
          setTlsVerify(configData?.tls_verify === false ? VERIFY_OPTIONS[1] : VERIFY_OPTIONS[0]);
          setCaCert(configData?.ca_cert ?? "");
        }
      }
    }
  }, [getConfig, confTmp]);

  const onPrem = type.id === "sharepoint-onprem";

  const onTypeSelected = (option) => {
    if (option.id === type.id) return;
    setSecret("");
    if (confType) {
      setBlock(option.id === "sharepoint-onprem" ? BLOCK_OPTIONS[1] : BLOCK_OPTIONS[0]);
    }
  };

  const buildConfig = () => {
    const common =
      authority &&
      secret &&
      bulk !== "" &&
      !isNaN(Number(bulk)) &&
      Number(maxsize) > 0 &&
      Number(maxsize) <= MAX_SIZE_LIMIT_MB &&
      block;
    if (!common) return null;
    if (onPrem) {
      if (!user) return null;
      if (caCert.trim() && !caCert.includes("BEGIN CERTIFICATE")) return null;
      if (caCert.includes("PRIVATE KEY")) return null;
      const library = normalizeLibraryUrl(authority);
      let host = "sharepoint";
      try {
        host = new URL(library).host || host;
      } catch (e) {}
      return {
        authority: library,
        user: user.trim(),
        secret: secret,
        params: RCLONE_PARAMS,
        server: server || host,
        type: type.id,
        bulk: Number(bulk),
        block: block.id,
        maxsize: Number(maxsize),
        tls_verify: tlsVerify.id === "yes",
        ca_cert: caCert.trim(),
      };
    }
    if (!(clientid && secretid && driveid && server)) return null;
    return {
      authority: authority,
      client_id: clientid,
      secret: secret,
      ["secret-id"]: secretid,
      drive_id: driveid,
      params: RCLONE_PARAMS,
      server: server,
      type: type.id,
      bulk: Number(bulk),
      block: block.id,
      maxsize: Number(maxsize),
    };
  };

  const addConfig = () => {
    const config = buildConfig();
    if (config) {
      const configString = JSON.stringify(config);
      const data = { appname: "rclshare", config: configString };

      dispatch(RequestSetConfig(data));
    } else {
      console.log("error");
    }
  };

  const updateConfigData = () => {
    const config = buildConfig();
    if (config) {
      const configString = JSON.stringify(config);
      const data = {
        appname: "rclshare",
        config: configString,
        mountpoint: getConfig?.config[confTmp]?.authority,
      };

      dispatch(RequestUpdateConfig(data));
    } else {
      console.log("error");
    }
  };

  const delConfig = () => {
    dispatch(
      RequestDeleteConfig({
        inttype: "rclshare",
        mountpoint: getConfig?.config[confTmp]?.authority,
      }),
    );
  };

  return show ? (
    <IntegrationMarketModal
      title="Sharepoint"
      onBack={() => history.push("/integration-market?bck=" + query.get("conf"))}
      onClose={closePopupHandler}>
      <div className="rclshare-settings">
        <div className="flex flex-align-items-center">
          <div className="flex-column m-r-10 relative">
            <p className="settings__label">Type</p>
            <CustomSelect
              data={TYPE_OPTIONS}
              select={type}
              setSelected={setType}
              onSelectItem={onTypeSelected}
              width={180}
              height={40}
            />
          </div>
          <div className="flex-column m-r-10">
            <Field
              label={onPrem ? "Library URL" : "Authority"}
              width="180px"
              value={authority}
              onChange={(e) => setAuthority(e.target.value)}
              placeholder={onPrem ? "https://sp.local/sites/x/Shared Documents" : "Enter authority"}
            />
          </div>
          <div className="flex-column">
            <Field
              label="Server"
              width="180px"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              placeholder={onPrem ? "Optional" : "Enter server"}
            />
          </div>
        </div>

        {onPrem ? (
          <div className="flex flex-align-items-center m-t-20">
            <div className="flex-column m-r-10">
              <Field
                label="User"
                width="180px"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                placeholder="DOMAIN\user"
                autoComplete="off"
              />
            </div>
            <div className="flex-column">
              <Field
                label="Password"
                width="180px"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Enter password"
                autoComplete="new-password"
              />
            </div>
          </div>
        ) : null}

        {onPrem ? (
          <div className="flex m-t-20">
            <div className="flex-column m-r-10 relative">
              <p className="settings__label">Verify Certificate</p>
              <CustomSelect
                data={VERIFY_OPTIONS}
                select={tlsVerify}
                setSelected={setTlsVerify}
                width={180}
                height={40}
              />
            </div>
            <div className="flex-column rclshare-settings__ca">
              <p className="settings__label">CA Certificate (PEM)</p>
              <textarea
                className="rclshare-settings__ca__input"
                rows={4}
                spellCheck={false}
                value={caCert}
                onChange={(e) => setCaCert(e.target.value)}
                placeholder="Optional: -----BEGIN CERTIFICATE----- ... -----END CERTIFICATE-----"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-align-items-center m-t-20">
              <div className="flex-column m-r-10">
                <Field
                  label="Client Id"
                  width="180px"
                  value={clientid}
                  onChange={(e) => setClientid(e.target.value)}
                  placeholder="Enter client id"
                />
              </div>
              <div className="flex-column m-r-10">
                <Field
                  label="Secret"
                  width="180px"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="Enter secret"
                />
              </div>
              <div className="flex-column">
                <Field
                  label="Secret Id"
                  width="180px"
                  value={secretid}
                  onChange={(e) => setSecretid(e.target.value)}
                  placeholder="Enter secret id"
                />
              </div>
            </div>
            <div className="flex flex-align-items-center m-t-20">
              <div className="flex-column">
                <Field
                  label="Drive Id"
                  width="180px"
                  value={driveid}
                  onChange={(e) => setDriveid(e.target.value)}
                  placeholder="Enter drive id"
                />
              </div>
            </div>
          </>
        )}

        <div className="flex flex-align-items-center m-t-20">
          <div className="flex-column m-r-10">
            <Field
              label="Bulk*"
              width="180px"
              type="number"
              value={bulk}
              onChange={(e) => setBulk(e.target.value)}
              placeholder="Enter bulk"
              tooltipText="Number of files scanned per bulk batch."
            />
          </div>
          <div className="flex-column relative">
            <p className="settings__label">Block</p>
            <CustomSelect
              data={BLOCK_OPTIONS}
              select={block}
              setSelected={setBlock}
              width={140}
              height={40}
            />
          </div>
        </div>

        <div className="flex flex-align-items-center m-t-20">
          <div className="flex-column m-r-10">
            <Field
              label="Max File Size (MB)*"
              width="180px"
              type="number"
              value={maxsize}
              onChange={(e) => setMaxsize(clampMaxsize(e.target.value))}
              placeholder="Enter max file size"
              tooltipText={`Files larger than this are not sent for analysis (at most ${MAX_SIZE_LIMIT_MB} MB).`}
            />
          </div>
        </div>
      </div>

      <div className="integration-market-modal__action">
        {confType ? (
          <button onClick={addConfig} className="primary-button">
            Add Config
          </button>
        ) : (
          <>
            <button onClick={updateConfigData} className="primary-button">
              Update
            </button>
            <button onClick={delConfig} className="danger-button">
              Delete
            </button>
          </>
        )}
      </div>

      {(loading || loadingUpdate) && query.get("conf") === "rclshare" && (
        <Backdrop show={loading || loadingUpdate} message="Please wait..." />
      )}
    </IntegrationMarketModal>
  ) : null;
};

export default RclshareSettings;
